import React, { useMemo, useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { GET_JOB_OFFER_UPERCUT, ADD_JOB_OFFER_DRAFT_PRO, ADD_JOB_OFFER_DRAFT_UPERCUT } from '@gql/jobs';
import { useGetJobOfferTemplate } from '@modules/JobOfferTemplates/hooks/useGetJobOfferTemplate';
import JobOfferDetail from '@modules/JobOffers/JobOfferDetail/JobOfferDetail';
import JobOfferCreditsModal from '@modules/JobOffers/components/JobOfferCreditsModal';
import { buildPayload } from '@modules/JobOffers/hooks/useSaveJobOffer';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';

const Container = styled(Box)({
    minHeight: 'calc(100dvh - 118px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    overflowY: 'auto',
});

const JobOfferAddModule = () => {
    const navigate = useNavigate();
    const { isUpercut } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const fromTemplate = searchParams.get('fromTemplate') ?? undefined;
    const fromJobOffer = searchParams.get('fromJobOffer') ?? undefined;

    const hasSource = !!(fromTemplate || fromJobOffer);

    const [creditsModalOpen, setCreditsModalOpen] = useState(false);
    const [savingModalDraft, setSavingModalDraft] = useState(false);

    const { template, loading: templateLoading, error: templateError } = useGetJobOfferTemplate(fromTemplate);

    const {
        data: jobOfferData,
        loading: jobOfferLoading,
        error: jobOfferError,
    } = useQuery(GET_JOB_OFFER_UPERCUT, {
        variables: { jobNumber: fromJobOffer, showInactive: true },
        skip: !fromJobOffer,
    });

    const loading = templateLoading || jobOfferLoading;
    const sourceData = fromTemplate ? template : fromJobOffer ? jobOfferData?.getJobOfferUpercut : undefined;
    const error = fromTemplate ? templateError : fromJobOffer ? jobOfferError : undefined;
    const notFound = !loading && hasSource && !sourceData;

    const proId: string | undefined = sourceData?.pro?._id;

    // Open modal once source data is ready
    useEffect(() => {
        if (!loading && hasSource && sourceData) {
            setCreditsModalOpen(true);
        }
    }, [loading, hasSource, sourceData]);

    const backPath = fromTemplate ? '/job-offer-templates' : '/job-offers';

    const [saveModalDraft] = useMutation(isUpercut ? ADD_JOB_OFFER_DRAFT_UPERCUT : ADD_JOB_OFFER_DRAFT_PRO, {
        onCompleted: result => {
            const saved = isUpercut ? result?.addJobOfferDraftUpercut : result?.addJobOfferDraftPro;
            setSavingModalDraft(false);
            setCreditsModalOpen(false);
            navigate(`/job-offers/${saved.jobNumber}/detail`);
        },
        onError: err => {
            setSavingModalDraft(false);
            enqueueSnackbar(err.message, { variant: 'error' });
        },
    });

    const handleModalContinue = async (featureId: string) => {
        if (!sourceData) return;
        setSavingModalDraft(true);
        const payload = buildPayload({ ...sourceData, featureId }, { ...sourceData, _id: undefined, featureId });
        await saveModalDraft({ variables: { payload } });
    };

    const initialJobOffer = useMemo(() => {
        if (!sourceData) return undefined;
        return {
            ...sourceData,
            _id: undefined,
        };
    }, [sourceData]);

    if (loading) {
        return (
            <Container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error || notFound) {
        return (
            <Container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="error">{error?.message ?? 'Ressource introuvable.'}</Typography>
            </Container>
        );
    }

    return (
        <Container>
            {hasSource && proId && (
                <JobOfferCreditsModal
                    open={creditsModalOpen}
                    proId={proId}
                    saving={savingModalDraft}
                    onContinue={handleModalContinue}
                    onClose={() => navigate(backPath)}
                />
            )}
            <JobOfferDetail
                initialJobOffer={initialJobOffer}
                onSaveSuccess={savedJobOffer => navigate(`/job-offers/${savedJobOffer.jobNumber}/detail`)}
            />
        </Container>
    );
};

export default JobOfferAddModule;
